<template>
	<Modal
		v-if="showBreakdownModal"
		:allow-close="true"
		:max-width="'max-w-[540px]'"
		:hide-actions="true"
		:prevent-scrolling="false"
		@modal:close="scorerStore.hideBreakdown"
	>
		<template #body>
			<div class="font-Inter">
				<div>
					<div class="flex items-center gap-[6px]">
						<ResumeScoreIcon />
						<h2 class="text-[24px] leading-[34px] font-bold text-won-black">Score Breakdown</h2>
					</div>
				</div>

				<div class="mt-5 flex items-center gap-2">
					<div class="text-[14px] ">
						<span class="flex items-center gap-1 font-semibold text-won-green">
							Overall Score: {{ getScore?.totalScore || 0 }}
							<span
								v-tooltip="{
									content: progressDescription,
									html: true,
									placement: 'bottom',
									popperClass: 'max-w-[190px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
								}"
							>
								<InfoIcon />
							</span>
						</span>
						<p class="text-won-black">
							Many factors are considered when determining resume score. Read the sections below to learn more about how resumes are scored.
						</p>
					</div>

					<div class="flex min-h-[77px] min-w-[77px] items-center justify-center">
						<ProgressCircle
							:progress="getScore?.totalScore || 0"
							:max-total="maxTotal"
							:circle-width="72"
							:stroke-width="5"
							:rotate="-90"
							:show-percents="false"
						/>
					</div>
				</div>

				<div class="mt-10 mb-[60px]">
					<div class="max-h-[300px] overflow-y-scroll! pr-4">

						<table class="w-full border-collapse">
							<tbody>
								<tr>
									<td class="flex flex-col gap-[14px] py-6 pr-[42px] text-base font-medium">
										<div class="w-full pr-[42px] pb-4">
											<span
												class="
													flex items-center gap-3 text-[16px] leading-[20px] font-bold
													whitespace-nowrap
												"
											>
												Content
												<CategoryProgress
													:total="30"
													:progress="getScore.contentScore"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Bullet point length</span>
											<span
												v-tooltip="{
													content: 'Each bullet should have enough detail — aim for 10+ words',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto] tracking-[0.5%]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Quantification</span>
											<span
												v-tooltip="{
													content: 'At least 60% of bullet points should include numbers',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Action verbs</span>
											<span
												v-tooltip="{
													content: 'Start bullet points with strong action verbs',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
									</td>

									<td class="w-[35%] pl-4">
										<div class="flex flex-col gap-[14px]">
											<div class="h-[36px] pb-4" />
											<EvaluationParameterStatus
												:status="contentBreakdown?.length?.breakdownMessage"
											/>
											<EvaluationParameterStatus
												:status="contentBreakdown?.statistics?.breakdownMessage"
											/>
											<EvaluationParameterStatus
												:status="contentBreakdown?.actionVerbs?.breakdownMessage"
											/>
										</div>
									</td>
								</tr>

								<tr class="h-10" />

								<tr>
									<td class="flex flex-col gap-[14px] py-6 pr-[42px] text-base font-medium">
										<div class="w-full pr-[42px] pb-4">
											<span
												class="
													flex items-center gap-3 text-[16px] leading-[20px] font-bold
													whitespace-nowrap
												"
											>
												Structure
												<CategoryProgress
													:total="20"
													:progress="getScore.structureScore"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Readability</span>
											<span
												v-tooltip="{
													content: 'Font size should be between 11-14 pt and the resume should not exceed 2 pages ',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Margins</span>
											<span
												v-tooltip="{
													content: 'Left and right margins should be equal',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Experience order</span>
											<span
												v-tooltip="{
													content: 'All experiences should be in reverse chronological order',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Section order</span>
											<span
												v-tooltip="{
													content: 'For students, the education section should be first. For non-students, the experience section should be first',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
									</td>

									<td class="pl-4">
										<div class="flex flex-col items-start justify-start gap-[14px]">
											<div class="h-[36px] pb-4" />
											<EvaluationParameterStatus
												:status="structureBreakdown?.readAbility?.breakdownMessage"
											/>
											<EvaluationParameterStatus
												:status="structureBreakdown?.margins?.breakdownMessage"
											/>
											<EvaluationParameterStatus
												:status="structureBreakdown?.experienceOrder?.breakdownMessage"
											/>
											<EvaluationParameterStatus
												:status="structureBreakdown?.sectionFormat?.breakdownMessage"
											/>
										</div>
									</td>
								</tr>

								<tr class="h-10" />

								<tr>
									<td class="flex flex-col gap-[14px] py-6 pr-[42px] text-base font-medium">
										<div class="w-full pr-[42px] pb-4">
											<span
												class="
													flex items-center gap-3 text-[16px] leading-[20px] font-bold
													whitespace-nowrap
												"
											>
												Completeness
												<CategoryProgress
													:total="10"
													:progress="getScore.completenessScore"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Personal Information</span>
											<span
												v-tooltip="{
													content: 'Your resume should have your name, phone number, and email',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Links</span>
											<span
												v-tooltip="{
													content: 'Your resume should have at least one URL',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Dates for each experience</span>
											<span
												v-tooltip="{
													content: 'Your resume should have dates given for all experiences',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
										<div class="flex w-fit items-center gap-1">
											<span class="whitespace-nowrap">3 bullet points per experience</span>
											<span
												v-tooltip="{
													content: 'Each experience should have at least 3 bullets',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Education Information</span>
											<span
												v-tooltip="{
													content: 'Education should include: Degree, University, Location',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Experience Information</span>
											<span
												v-tooltip="{
													content: 'Experiences should include: Job title, Company/Organization, Location',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
									</td>

									<td class="pl-4">
										<div class="flex flex-col items-start justify-start gap-[14px]">
											<div class="h-[36px] pb-4" />
											<EvaluationParameterStatus
												:status="completenessBreakdown?.personalInformation?.breakdownMessage"
											/>
											<EvaluationParameterStatus
												:status="completenessBreakdown?.hasUrl?.breakdownMessage"
											/>
											<EvaluationParameterStatus
												:status="completenessBreakdown?.datesForExperiences?.breakdownMessage"
											/>
											<EvaluationParameterStatus
												:status="completenessBreakdown?.bulletPoints?.breakdownMessage"
											/>
											<EvaluationParameterStatus
												:status="completenessBreakdown?.educationInfo?.breakdownMessage"
											/>
											<EvaluationParameterStatus
												:status="completenessBreakdown?.experienceInfo?.breakdownMessage"
											/>
										</div>
									</td>
								</tr>

								<tr class="h-10" />

								<tr class="relative">
									<td class="flex flex-col gap-[14px] py-6 pr-[42px] text-base font-medium">
										<div class="w-full pr-[42px] pb-4">
											<span
												class="
													flex items-center gap-3 text-[16px] leading-[20px] font-bold
													whitespace-nowrap
												"
											>
												Alignment
												<CategoryProgress
													:total="40"
													:progress="getScore.alignmentScore"
												/>
											</span>
										</div>

										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">Required Skills</span>
											<span
												v-tooltip="{
													content: 'Skills the target role requires — matched from your experience',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>

										<div class="flex items-center gap-1">
											<span class="whitespace-nowrap">
												Nice-to-Have Skills
											</span>
											<span
												v-tooltip="{
													content: 'Skills that strengthen your fit for the target role',
													placement: 'top',
													popperClass: 'max-w-[250px] text-center! text-[12px]! leading-[16px]! font-[Roboto]'
												}"
											>
												<InfoIcon
													color="#ACADAE"
												/>
											</span>
										</div>
									</td>

									<td class="pl-4">
										<div
											v-if="alignmentStatus === 'scored'"
											class="flex flex-col gap-[14px]"
										>
											<div class="h-[36px] pb-4" />
											<EvaluationParameterStatus
												:status="requiredSkillsStatus"
											/>

											<EvaluationParameterStatus
												:status="niceToHaveSkillsStatus"
											/>
										</div>

										<div
											v-if="alignmentStatus !== 'scored'"
											class="
												overlay absolute top-0 left-0 flex size-full items-center
												justify-center bg-white/80
											"
										>
											<button
												:class="[
													'h-8 rounded bg-won-green px-2.5 py-1.5 text-p-base text-white',
													'flex items-center justify-center font-semibold'
												]"
												@click="scorerStore.hideBreakdown"
											>
												{{ alignmentCtaLabel }}
											</button>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>

				<div
					class="
						absolute bottom-0 left-0 flex w-full items-center justify-end
						gap-11 bg-won-green-light px-5 py-3
					"
				>
					<button
						class="rounded bg-won-green px-[31px] py-[9px] text-white"
						@click="scorerStore.hideBreakdown"
					>
						Close
					</button>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useResumeScorerStore } from '../../store/scorer.store';

import Modal from '../../components/modal.vue';
import ResumeScoreIcon from '../../icons/resume-score-icon.vue';
import ProgressCircle from '../../components/resume-score/score-circle.vue';
import InfoIcon from '../../icons/info-icon.vue';
import CategoryProgress from './category-progress.vue';

import EvaluationParameterStatus from '../../components/resume-score/evaluation-parameter-status.vue';

const progressDescription = '<p>Download your resume once you’ve reached a green score!</p><br><i>Green: 75-100%<br>Orange: 50-74%<br>Red: 0-49%<br></i>';

const scorerStore = useResumeScorerStore();

const { breakdown, getScore, maxTotal, alignmentStatus, showBreakdownModal } = storeToRefs(scorerStore);

const contentBreakdown = computed(() => breakdown.value.content);

const structureBreakdown = computed(() => breakdown.value.structure);

const completenessBreakdown = computed(() => breakdown.value.completeness);

// Alignment leaves carry a numeric score (out of ALIGNMENT_REQUIRED=25 /
// ALIGNMENT_NICE_TO_HAVE=15), not a `breakdownMessage` like the other three
// pillars — derive the same fullyMet/partiallyMet/notMet vocabulary so
// EvaluationParameterStatus can render it consistently.
const statusFor = (score, max) => {
	if (!score) {return 'notMet';}
	if (score >= max) {return 'fullyMet';}
	return 'partiallyMet';
};

const requiredSkillsStatus = computed(() => statusFor(breakdown.value?.alignment?.requiredScore, 25));
const niceToHaveSkillsStatus = computed(() => statusFor(breakdown.value?.alignment?.niceToHaveScore, 15));

// Alignment only ever scores once a target is set and resolves with High
// classifier confidence — closing the modal surfaces the sidebar's own
// role form (EN-814) rather than duplicating it here. At Medium confidence
// that form is pre-filled with the closest specialization, so that's the
// one to confirm.
const alignmentCtaLabel = computed(() => {
	if (alignmentStatus.value === 'needs_confirmation') {
		return `Confirm "${breakdown.value?.alignment?.specialization ?? 'closest match'}" in sidebar`;
	}

	if (alignmentStatus.value === 'unavailable') {
		return 'Alignment temporarily unavailable';
	}

	return 'Set Target Job in sidebar';
});
</script>

<style scoped>
    table {
        width: 100%;
        table-layout: fixed;
    }

    th, td, tr {
        padding: 0;
    }
</style>
